import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, TrendingUp, Users, Building2, Euro, CreditCard,
  Award, Activity, Gift, Filter, Download, AlertCircle, CheckCircle2,
  Sparkles, Calendar, BarChart3, Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import { apiFetch } from '../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (n) => new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(n || 0));

const fmtNum = (n) => new Intl.NumberFormat('de-DE').format(Number(n || 0));

const fmtPct = (n) => `${(Number(n || 0)).toFixed(1)}%`;

const fmtDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
};

// Mini Sparkline-Chart (CSS only)
const Sparkline = ({ data, accessor, height = 60, color = '#eab308', testid }) => {
  const values = useMemo(() => (data || []).map((d) => Number(accessor(d) || 0)), [data, accessor]);
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-[2px] w-full" style={{ height }} data-testid={testid}>
      {values.map((v, i) => {
        const h = max ? Math.max(2, (v / max) * height) : 2;
        return (
          <div
            key={i}
            className="flex-1 rounded-sm transition-all"
            style={{ height: `${h}px`, background: color, opacity: v > 0 ? 0.9 : 0.15 }}
            title={`${data[i]?.date}: ${v}`}
          />
        );
      })}
    </div>
  );
};

const KpiCard = ({ label, value, sub, icon: Icon, accent = 'yellow', testid }) => {
  const colorMap = {
    yellow: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    green: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    blue: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
    rose: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30' }[accent] || 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
  return (
    <Card className="bg-gray-950/80 border border-gray-800 hover:border-gray-700 transition-colors" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-white truncate" data-testid={`${testid}-value`}>{value}</p>
            {sub ? <p className="text-xs text-gray-500">{sub}</p> : null}
          </div>
          <div className={`p-2 rounded-lg border ${colorMap}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminInsightsPage = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/admin/analytics-pro?days=${days}`, {
        headers: {} });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.detail || 'Konnte Daten nicht laden');
      }
      setData(json);
    } catch (err) {
      toast.error(err.message || 'Fehler beim Laden der Analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [days]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['Firma', 'Email', 'Seats gesamt', 'Seats genutzt', 'Frei', 'Auslastung %', 'Status', 'Cycle', 'Created'],
      ...data.license_utilization.map((c) => [
        c.name, c.email, c.seats_total, c.seats_used, c.seats_free,
        c.utilization_pct, c.subscription_status, c.billing_cycle, c.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smartvolt-lizenzen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLicenses = useMemo(() => {
    const list = data?.license_utilization || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q),
    );
  }, [data, search]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
      </div>
    );
  }

  const o = data?.overview || {};
  const r = data?.revenue || {};
  const m = data?.mrr || {};
  const f = data?.funnel || {};
  const l = data?.learning || {};
  const ref = data?.referrals || {};
  const topRef = data?.top_referrers || [];
  const growth = data?.growth || {};

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_rgba(234,179,8,0.08)_0%,_transparent_50%),_linear-gradient(to_bottom,_#000_0%,_#0a0a0a_100%)] text-white">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/admin')}
              className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10"
              data-testid="insights-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Admin
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              <div>
                <h1 className="text-lg font-bold tracking-tight">Live-Insights</h1>
                <p className="text-xs text-gray-500">SmartVolt Academy · Plattform-Cockpit</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 bg-gray-900/80 border border-gray-800 rounded-lg p-1">
              {[7, 30, 90, 180, 365].map((n) => (
                <button
                  key={n}
                  onClick={() => setDays(n)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    days === n ? 'bg-yellow-500 text-black font-semibold' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid={`range-${n}d`}
                >
                  {n}T
                </button>
              ))}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={load}
              className="border-gray-700 text-gray-200 hover:bg-gray-800"
              data-testid="insights-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="admin-insights-page">
        {/* Live indicator */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            Live · letzte Aktualisierung {new Date(data?.generated_at || Date.now()).toLocaleString('de-DE')}
          </div>
          {company?.name && (
            <span className="text-xs text-gray-500">Angemeldet als <span className="text-yellow-500">{company.email}</span></span>
          )}
        </div>

        {/* === HERO KPIs === */}
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Umsatz &amp; Wachstum</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Lifetime Umsatz"
              value={fmtEUR(r.total_lifetime_eur)}
              sub={r.is_estimated ? '⚠ geschätzt (kein IPN-Amount)' : 'aus IPN-Logs'}
              icon={Euro} accent="green"
              testid="kpi-revenue-total"
            />
            <KpiCard
              label="Monatlich (MRR)"
              value={fmtEUR(m.monthly_recurring_eur)}
              sub={`Jahres-Run-Rate ${fmtEUR(m.annualized_eur)}`}
              icon={TrendingUp} accent="yellow"
              testid="kpi-mrr"
            />
            <KpiCard
              label="Aktive Abos"
              value={fmtNum(m.active_subscriptions)}
              sub={`${fmtNum(m.total_seats_sold)} verkaufte Lizenzen`}
              icon={CreditCard} accent="blue"
              testid="kpi-active-subs"
            />
            <KpiCard
              label="Umsatz heute"
              value={fmtEUR(r.today_eur)}
              sub={`MTD ${fmtEUR(r.month_to_date_eur)}`}
              icon={Calendar} accent="purple"
              testid="kpi-revenue-today"
            />
          </div>
        </section>

        {/* === Plattform-Übersicht === */}
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Plattform-Übersicht</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard label="Firmen" value={fmtNum(o.total_companies)} icon={Building2} accent="cyan" testid="kpi-companies" />
            <KpiCard label="Mitarbeiter" value={fmtNum(o.total_employees)} icon={Users} accent="yellow" testid="kpi-employees" />
            <KpiCard label="Kurse" value={fmtNum(o.total_courses)} icon={BarChart3} accent="purple" testid="kpi-courses" />
            <KpiCard label="Lernmodule" value={fmtNum(o.total_modules)} icon={Activity} accent="blue" testid="kpi-modules" />
            <KpiCard label="Zertifikate" value={fmtNum(o.total_certificates)} icon={Award} accent="green" testid="kpi-certs" />
            <KpiCard
              label="Empfehlungen"
              value={fmtNum(ref.qualified)}
              sub={`${fmtEUR(ref.total_payout_eur)} Auszahlung offen`}
              icon={Gift} accent="rose"
              testid="kpi-referrals"
            />
          </div>
        </section>

        {/* === Charts: Umsatz & Wachstum === */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-gray-950/80 border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Euro className="h-4 w-4 text-emerald-500" />
                  Umsatz · letzte {days} Tage
                </CardTitle>
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
                  {fmtNum((r.by_day || []).reduce((s, d) => s + (d.orders || 0), 0))} Orders
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Sparkline
                data={r.by_day}
                accessor={(d) => d.amount}
                color="#10b981"
                testid="chart-revenue"
              />
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>{fmtDateShort(r.by_day?.[0]?.date)}</span>
                <span>{fmtDateShort(r.by_day?.[r.by_day?.length - 1]?.date)}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-950/80 border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-sky-500" />
                  Neue Firmen · letzte {days} Tage
                </CardTitle>
                <Badge variant="outline" className="border-sky-500/30 text-sky-400">
                  {fmtNum((growth.companies || []).reduce((s, d) => s + (d.count || 0), 0))} Registrierungen
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Sparkline
                data={growth.companies}
                accessor={(d) => d.count}
                color="#0ea5e9"
                testid="chart-companies"
              />
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>{fmtDateShort(growth.companies?.[0]?.date)}</span>
                <span>{fmtDateShort(growth.companies?.[growth.companies?.length - 1]?.date)}</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* === Tabs Content === */}
        <Tabs defaultValue="funnel" className="w-full">
          <TabsList className="bg-gray-950/80 border border-gray-800 flex-wrap h-auto">
            <TabsTrigger value="funnel" data-testid="tab-funnel" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400">
              <Target className="h-4 w-4 mr-2" /> Funnel
            </TabsTrigger>
            <TabsTrigger value="licenses" data-testid="tab-licenses" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400">
              <CreditCard className="h-4 w-4 mr-2" /> Lizenzen ({(data?.license_utilization || []).length})
            </TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400">
              <Activity className="h-4 w-4 mr-2" /> Lernaktivität
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400">
              <Gift className="h-4 w-4 mr-2" /> Empfehlungen
            </TabsTrigger>
          </TabsList>

          {/* --- Funnel --- */}
          <TabsContent value="funnel" className="mt-4">
            <Card className="bg-gray-950/80 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Conversion-Funnel</CardTitle>
                <CardDescription className="text-gray-400">
                  Vom Registrierungs-Klick bis zum bezahlten Aktiv-Abo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: 'Registrierungen', value: f.registrations, color: 'bg-sky-500/80', pct: 100 },
                  {
                    label: 'Checkout gestartet',
                    value: f.checkouts_started,
                    color: 'bg-yellow-500/80',
                    pct: f.registrations ? (f.checkouts_started / f.registrations) * 100 : 0 },
                  {
                    label: 'Bezahlt & Aktiv',
                    value: f.active_paid,
                    color: 'bg-emerald-500/80',
                    pct: f.registrations ? (f.active_paid / f.registrations) * 100 : 0 },
                ].map((step, i) => (
                  <div key={step.label} data-testid={`funnel-step-${i}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-300">{step.label}</span>
                      <span className="text-white font-semibold">
                        {fmtNum(step.value)} <span className="text-gray-500 text-xs">({fmtPct(step.pct)})</span>
                      </span>
                    </div>
                    <div className="h-3 bg-gray-900 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${step.color} rounded-full transition-all duration-500`}
                        style={{ width: `${Math.max(2, step.pct)}%` }}
                      />
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-3 pt-4 mt-2 border-t border-gray-800">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-400">{fmtPct(f.register_to_checkout_pct)}</p>
                    <p className="text-xs text-gray-500">Register → Checkout</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-400">{fmtPct(f.checkout_to_paid_pct)}</p>
                    <p className="text-xs text-gray-500">Checkout → Bezahlt</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{fmtPct(f.register_to_paid_pct)}</p>
                    <p className="text-xs text-gray-500">Register → Bezahlt</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* MRR breakdown */}
            <Card className="bg-gray-950/80 border-gray-800 mt-4">
              <CardHeader>
                <CardTitle className="text-white">MRR-Aufschlüsselung</CardTitle>
                <CardDescription className="text-gray-400">
                  Monatlich wiederkehrender Umsatz nach Abrechnungszyklus
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {['monthly', 'yearly'].map((key) => {
                    const v = m.by_billing_cycle?.[key] || { count: 0, seats: 0, mrr: 0 };
                    return (
                      <div key={key} className="bg-black/40 rounded-lg p-4 border border-gray-800" data-testid={`mrr-${key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400 uppercase">{key === 'monthly' ? 'Monatlich' : 'Jährlich'}</span>
                          <Badge variant="outline" className="border-gray-700 text-gray-300">{v.count} Abos</Badge>
                        </div>
                        <p className="text-2xl font-bold text-white">{fmtEUR(v.mrr)}<span className="text-sm text-gray-500"> /Monat</span></p>
                        <p className="text-xs text-gray-500 mt-1">{fmtNum(v.seats)} verkaufte Seats</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* --- Lizenzen --- */}
          <TabsContent value="licenses" className="mt-4">
            <Card className="bg-gray-950/80 border-gray-800">
              <CardHeader className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-white">Lizenz-Auslastung pro Firma</CardTitle>
                    <CardDescription className="text-gray-400">
                      Wer hat wie viele Seats gekauft &amp; wie stark werden sie genutzt?
                    </CardDescription>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={exportCsv}
                    className="border-gray-700 text-gray-200 hover:bg-gray-800"
                    data-testid="export-licenses-csv"
                  >
                    <Download className="h-4 w-4 mr-2" /> CSV exportieren
                  </Button>
                </div>
                <div className="relative">
                  <Filter className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <Input
                    placeholder="Firma oder E-Mail suchen..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-black/40 border-gray-800 text-white"
                    data-testid="license-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 text-gray-500 uppercase text-xs">
                        <th className="text-left py-2 px-2">Firma</th>
                        <th className="text-left py-2 px-2 hidden md:table-cell">E-Mail</th>
                        <th className="text-center py-2 px-2">Seats</th>
                        <th className="text-left py-2 px-2 w-40">Auslastung</th>
                        <th className="text-center py-2 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLicenses.length === 0 && (
                        <tr><td colSpan="5" className="text-center py-8 text-gray-500">Keine Treffer</td></tr>
                      )}
                      {filteredLicenses.map((c) => (
                        <tr key={c.company_id} className="border-b border-gray-900 hover:bg-gray-900/40" data-testid={`license-row-${c.company_id}`}>
                          <td className="py-3 px-2">
                            <div className="font-medium text-white">{c.name}</div>
                            <div className="text-xs text-gray-500 md:hidden">{c.email}</div>
                          </td>
                          <td className="py-3 px-2 text-gray-400 hidden md:table-cell">{c.email}</td>
                          <td className="py-3 px-2 text-center">
                            <span className="text-white font-semibold">{c.seats_used}</span>
                            <span className="text-gray-500"> / {c.seats_total}</span>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    c.utilization_pct >= 90 ? 'bg-rose-500' :
                                    c.utilization_pct >= 50 ? 'bg-yellow-500' : 'bg-emerald-500'
                                  }`}
                                  style={{ width: `${Math.min(100, c.utilization_pct)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 w-12 text-right">{fmtPct(c.utilization_pct)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            {c.subscription_status === 'active' ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Aktiv
                              </Badge>
                            ) : c.subscription_status === 'none' ? (
                              <Badge className="bg-gray-700/40 text-gray-400 border-gray-600 border">Kein Abo</Badge>
                            ) : (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 border">
                                <AlertCircle className="h-3 w-3 mr-1" /> {c.subscription_status}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* --- Lernaktivität --- */}
          <TabsContent value="courses" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Anmeldungen" value={fmtNum(l.total_enrollments)} icon={Users} accent="blue" testid="kpi-enrollments" />
              <KpiCard label="Abschlüsse" value={fmtNum(l.completed_enrollments)} icon={Award} accent="green" testid="kpi-completions" />
              <KpiCard label="Abschlussrate" value={fmtPct(l.completion_rate_pct)} icon={Target} accent="yellow" testid="kpi-completion-rate" />
              <KpiCard label="Lektionen abgeschlossen" value={fmtNum(l.lesson_completions)} icon={Activity} accent="purple" testid="kpi-lesson-done" />
            </div>

            <Card className="bg-gray-950/80 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Top-Kurse nach Anmeldungen</CardTitle>
                <CardDescription className="text-gray-400">Welche Kurse werden am meisten besucht und abgeschlossen?</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(l.top_courses || []).map((c, i) => (
                    <div key={c.id} className="bg-black/40 rounded-lg p-3 border border-gray-800" data-testid={`top-course-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-gray-500 font-mono text-sm w-6 text-right">#{i + 1}</span>
                          <span className="text-white font-medium truncate">{c.title}</span>
                        </div>
                        <div className="flex gap-4 text-xs">
                          <span className="text-sky-400">{fmtNum(c.enrolled)} Anm.</span>
                          <span className="text-emerald-400">{fmtNum(c.completed)} ✓</span>
                          <span className="text-yellow-400">{fmtPct(c.completion_rate)}</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-yellow-500 to-emerald-500 rounded-full"
                          style={{ width: `${Math.min(100, c.completion_rate)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  {(l.top_courses || []).length === 0 && (
                    <p className="text-gray-500 text-center py-6">Noch keine Anmeldungen</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* --- Referrals --- */}
          <TabsContent value="referrals" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Empfehlungen gesamt" value={fmtNum(ref.total)} icon={Gift} accent="rose" testid="kpi-ref-total" />
              <KpiCard label="Registriert" value={fmtNum(ref.registered)} icon={Users} accent="blue" testid="kpi-ref-registered" />
              <KpiCard label="Qualifiziert (zahlend)" value={fmtNum(ref.qualified)} icon={CheckCircle2} accent="green" testid="kpi-ref-qualified" />
              <KpiCard label="Offene Auszahlung" value={fmtEUR(ref.total_payout_eur)} icon={Euro} accent="yellow" testid="kpi-ref-payout" />
            </div>

            <Card className="bg-gray-950/80 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Top-Empfehler</CardTitle>
                <CardDescription className="text-gray-400">
                  50&nbsp;€ Bonus pro qualifizierter (zahlender) Empfehlung
                </CardDescription>
              </CardHeader>
              <CardContent>
                {topRef.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">Noch keine Empfehlungen</p>
                ) : (
                  <div className="space-y-2">
                    {topRef.map((r2, i) => (
                      <div key={r2.email} className="flex items-center justify-between bg-black/40 rounded-lg p-3 border border-gray-800" data-testid={`top-ref-${i}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`text-2xl font-bold ${
                            i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-700' : 'text-gray-600'
                          }`}>
                            {i + 1}.
                          </span>
                          <div className="min-w-0">
                            <p className="text-white text-sm truncate">{r2.email}</p>
                            <p className="text-xs text-gray-500">{r2.total} Empfehlungen · {r2.qualified} qualifiziert</p>
                          </div>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border">
                          {fmtEUR(r2.payout_eur)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center text-xs text-gray-600 py-4">
          SmartVolt Academy · Live-Cockpit · v1.0
        </div>
      </main>
    </div>
  );
};

export default AdminInsightsPage;
